import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from 'store';
import { Config, Routes } from 'config';
import { useCourseContext } from 'context';
import { selectAuthUser, selectIsGuestUser } from 'store/slices/auth';

import { Icon } from '../icons';
import { Button } from '../button';
import { LogoutButton } from '../logout-button';

import { StepButton } from './step-button';
import { BurgerButton } from './burger-button';
import { NavigationLink } from './navigation-link';
import { LanguageButton } from './language-button';
import { FundingAcknowledgmentButton } from './funding-acknowledgment-button';

const links = [
  {
    label: 'Contact Us',
    i18nKey: 'contactUs',
    href: Routes.ContactUs.Home,
  },
  {
    label: 'Dashboard',
    i18nKey: 'dashboard',
    href: Routes.Dashboard.Home,
  },
];

const commonButtonStyle =
  'flex h-8 w-8 min-w-0 items-center justify-center rounded-full sm:h-10 sm:w-10';

export const TopNavigation = () => {
  const { steps } = useCourseContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const authUser = useAppSelector(selectAuthUser);
  const isGuestUser = useAppSelector(selectIsGuestUser);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation(['header']);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="relative">
      <div
        className={twMerge(
          'flex h-12 w-full items-center justify-between gap-x-2 bg-white px-4 md:h-16 xl:py-3 xl:pl-6',
          isMobileMenuOpen
            ? 'rounded-t-xl xl:rounded-xl'
            : 'lx:rounded-3xl rounded-xl'
        )}
      >
        <div className="flex items-center">
          <Link to={Routes.Dashboard.Home}>
            <img
              src="/logo.svg"
              alt="Seahl Logo"
              className="h-auto w-11 md:w-14"
            />
          </Link>
          {Config.showTenantLogo && authUser?.tenant?.logo && (
            <div className="ml-2 h-8 w-fit max-w-20 md:ml-4 md:h-10 md:max-w-40">
              <img
                src={authUser.tenant.logo}
                alt={authUser.tenant.name}
                className="h-full w-auto object-contain"
              />
            </div>
          )}
          <div className="hidden flex-1 pl-8 xl:flex">
            {links.map((link) => (
              <NavigationLink
                key={link.href}
                href={link.href}
                variant="desktop"
                label={t(link.i18nKey)}
                isActive={pathname === link.href}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center">
          <div className="flex space-x-2 md:space-x-3.5">
            {steps?.map((item) => (
              <StepButton
                item={item}
                key={item.id}
                onClick={() => {
                  const { id, resourceVersions } = item;
                  const isResource =
                    resourceVersions && resourceVersions.length > 0;
                  if (isResource) {
                    navigate(Routes.Course.Resource.replace(':id', id));
                  } else {
                    navigate(Routes.Course.Form.replace(':id', id));
                  }
                }}
              />
            ))}
          </div>
          {Config.showFundingAcknowledgment && (
            <FundingAcknowledgmentButton className="ml-6 hidden xl:flex" />
          )}
          <LanguageButton className="ml-6 hidden xl:flex" />
          {isGuestUser ? (
            <LogoutButton className="ml-6 hidden xl:flex" />
          ) : (
            <Button
              component={Link}
              color="secondary"
              to={Routes.Profile.Home}
              className={twMerge(commonButtonStyle, 'ml-6 hidden xl:flex')}
            >
              <Icon.User />
            </Button>
          )}
          <BurgerButton
            isOpen={isMobileMenuOpen}
            className="ml-4 xl:hidden"
            onClick={handleMobileMenuToggle}
          />
        </div>

        {isMobileMenuOpen && (
          <div className="absolute top-full right-0 left-0 z-50 rounded-b-3xl border-t-0 border-gray-100 bg-white p-4 shadow-lg xl:hidden">
            {!isGuestUser && (
              <NavigationLink
                variant="mobile"
                className="h-10"
                label={t('account')}
                href={Routes.Profile.Home}
                onClick={handleMobileMenuClose}
                isActive={pathname === Routes.Profile.Home}
                icon={
                  <div className="bg-blue-secondary-primary hover:bg-blue-secondary-hover flex h-10 w-10 items-center justify-center rounded-full">
                    <Icon.User />
                  </div>
                }
              />
            )}

            {links.map((item) => (
              <NavigationLink
                key={item.href}
                href={item.href}
                variant="mobile"
                label={t(item.i18nKey)}
                onClick={handleMobileMenuClose}
                isActive={pathname === item.href}
              />
            ))}

            {Config.showFundingAcknowledgment && (
              <FundingAcknowledgmentButton className="mt-4" />
            )}
            <LanguageButton className="mt-4" />

            {isGuestUser && <LogoutButton className="mt-4" />}
          </div>
        )}
      </div>
    </div>
  );
};
